import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	EntitySearchAndDetailModel,
	DefinitionUtils,
	EntityDetailModel,
	styled,
	FormDefinition as Form,
	FormItemDefinition as FormItem,
	CrossDefinition as Cross,
	CentererDefinition as Centerer,
	MessageDefinition as Message,
	ButtonDefinition as Button,
	DateUtils,
	REPORTED_EXCEPTION,
	updateEntityTimestamp,
	showMessageBox,
	ValueValidator,
	ContextValidator,
} from 'ps-gui';

import { getPhrase } from '../data/phrases';
import {
	createSearchModel,
	normalizePhoneNumber
} from './utils';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('UserAdmin');

export default class UserAdminModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setInitialized() {
		return new UserAdminModel(this.#data.set('initialized', true));
	}

	initialize() {
		let srch = createSearchModel('/api/dq/sv_native_user');
		return this.setEntitySearchAndDetail(
			new EntitySearchAndDetailModel()
				.setSearch(srch)
				.setDetail(getDetailModel())
				.setNewRowMode(EntitySearchAndDetailModel.NEW_ROW_MODE_EMPTY)
				.setEntityName('user')
		).#setInitialized();
	}

	setEntitySearchAndDetail(model) {
		return new UserAdminModel(this.#data.set('esad', model.getState()));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

let detailDefinition = Centerer(Form().setOpacity(ctx => ctx.getProperty('isDeleted') === 'Y' ? '0.3' : '1').setVirtual(false).setBasePath('detail').setItems([
	Cross().setItems([
		FormItem('userName', 'text')
			.setMandatory(true)
			.setMaxLength(200)
			.setReadOnly(ctx => ctx.getProperty('id'))
			.setValidator(ValueValidator(value => {
				if (value && value.replace(/\s/g, '') !== value) {
					return getPhrase('userNameMustNotContainSpaces');
				} else {
					return null;
				}
			}))
			.onChange((ctx, me, value) => {
				if (value && value.indexOf('@') > 0) {
					ctx.setProperty('email', value);
				}
			}),
		FormItem('displayName', 'text').setMandatory(true)
	]),
	Cross().setItems([
		FormItem('fromDate', 'date').setMandatory(true).setWithTime(true).setReadOnly(true),
		FormItem('toDate', 'date').setWithTime(true).setReadOnly(true),
	]).setVisible(ctx => ctx.getProperty('id')),
	Cross().setItems([
		Button('terminate', getPhrase('terminate')).setListener(async e => {
			const ctx = e.ctx;
			const res = await webapi.execute('/api/proc/api_user/terminateUser', {
				id: ctx.getProperty('id')
			});
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			updateEntityTimestamp('user');
			ctx.setModel(await ctx.getModel().reload());
		}).setVisible(ctx => !ctx.getProperty('toDate') && ctx.getProperty('id')).setDisabled(ctx => ctx.getRootProperty('detail') !== ctx.getRootProperty('originalDetail')),
		Button('reactivate', getPhrase('reactivate')).setListener(async e => {
			const ctx = e.ctx;
			const res = await webapi.execute('/api/proc/api_user/reactivateUser', {
				id: ctx.getProperty('id')
			});
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			updateEntityTimestamp('user');
			ctx.setModel(await ctx.getModel().reload());
		}).setVisible(ctx => ctx.getProperty('toDate') && ctx.getProperty('id')).setDisabled(ctx => ctx.getRootProperty('detail') !== ctx.getRootProperty('originalDetail')),
		Button('impersonate', getPhrase('loginAsUser')).setListener(async e => {
			const ctx = e.ctx;
			const res = await webapi.execute('/api/proc/api_user/impersonateUser', {
				userId: ctx.getProperty('id')
			});
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			const url = window.basePath + '?s=' + res.token;
			window.open(url, '_blank', 'noreferrer,noopener');
		}).setType('light').setVisible(ctx => !ctx.getProperty('toDate') && ctx.getProperty('id')).setDisabled(ctx => ctx.getRootProperty('detail') !== ctx.getRootProperty('originalDetail')),
	]),
	Cross().setItems([
		FormItem('phone', 'text')
			.setMandatory(true)
			.onChange((ctx, me, value) => {
				const normalized = normalizePhoneNumber(value);
				if (normalized && normalized !== value) {
					ctx.setProperty('phone', normalized);
				}
			}),
		FormItem('email', 'text').setMandatory(true),
		FormItem('languageId', 'lookup').setLookup('language')
	]),
	Cross().setItems([
		FormItem('isSystemAdmin', 'lookup').setLookup('ynBoolean').setMandatory(true),
		FormItem('isSubmissionAdmin', 'lookup').setLookup('ynBoolean').setMandatory(true),
	]).setValidator(ContextValidator((errors, ctx, itemName) => {
		let itemNames = itemName ? [itemName] : ['isSystemAdmin', 'isSubmissionAdmin'];
		const email = (ctx.getProperty('email') || '').toLowerCase();
		const canBeAdmin = email.endsWith('@raventia.cz') || email.endsWith('@intergram.cz');
		itemNames.forEach(name => {
			const value = ctx.getProperty(name);
			errors.add(
				ctx.getFullPath(name),
				value === 'Y' && !canBeAdmin ? getPhrase('onlyIntergramAndRaventiaUsersCanBeAdmins') : null,
				'cantBeAdmin'
			);
		});
	})),
	Cross().setItems([FormItem('comments', 'text').setMultiline(true)]),
	Cross().setItems([FormItem('parties', 'lookupList').setLookup('partyIdName')]),
	Message(ctx => {
		const bis = ctx.getProperty('bankIdentityStats');
		if (!bis) {
			return null;
		}
		const activeCount = bis.get('activeCount');
		const totalCount = bis.get('totalCount');
		if (activeCount > 0) {
			return (
				<ActivationMessage>{getPhrase('profileIsLinkedToBankIdentity')}</ActivationMessage>
			);
		}
		if (totalCount > 0) {
			return (
				<ActivationMessage>{getPhrase('profileWasLinkedToBankIdentity')}</ActivationMessage>
			);
		}
		return null;
	}),
	Cross().setItems([Button('sendActivationLink', getPhrase('sendActivationLink')).setListener(async e => {
		const ctx = e.ctx;
		const dlgRes = await showMessageBox(getPhrase('reallySendActivationLink'), {
			type: 'confirm'
		});
		if (dlgRes !== 'yes') {
			return;
		}
		const res = await webapi.execute('/api/user/createActivationAuthenticationCode', {
			userId: ctx.getProperty('id'),
			languageId: ctx.getProperty('languageId')
		});
		if (res === REPORTED_EXCEPTION) {
			return;
		}
		await showMessageBox(getPhrase('activationLinkSent'));
		ctx.setModel(await ctx.getModel().reload());
		updateEntityTimestamp('authenticationCode');
	}).setVisible(ctx => ctx.getProperty('id') && !ctx.getProperty('toDate') && !ctx.getProperty(['activation', 'activatedWhen']))]).setJustifyContent('right'),
	Message(ctx => {
		const activatedWhen = ctx.getProperty(['activation', 'activatedWhen']);
		const unusedActivationFrom = ctx.getProperty(['activation', 'unusedActivationFrom']);
		console.log('phone = ', ctx.getProperty('phone'));
		if (activatedWhen) {
			return (
				<ActivationMessage>{getPhrase('userActivated').replace('$when', DateUtils.formatDate(activatedWhen))}</ActivationMessage>
			);
		}
		if (unusedActivationFrom) {
			return (
				<ActivationMessage>{getPhrase('userActivationSent').replace('$when', DateUtils.formatDate(unusedActivationFrom))}</ActivationMessage>
			);
		}
		return null;
	})
])).setWidth('96%').setMaxWidth('1000px');

const ActivationMessage = styled.div`
	color: ${props => props.theme.lightButtonColor};
	font-weight: 500;

	margin: 8px;
	text-align: right;
`;

let detailModel = null;

export function getDetailModel() {
	if (!detailModel) {
		DefinitionUtils.setLabels(detailDefinition);

		detailModel = new EntityDetailModel()
			.setLoadUri('/api/proc/api_user/getUserDetail')
			.setSaveUri('/api/proc/api_user/saveUser')
			.setDefinition(detailDefinition)
			.setEntityName('user');
	}
	return detailModel;
}

